/**
 * ReviewQueueSection — one headed section of the desktop queue (BON-381).
 *
 * A booking record exists and a checkout session is one that never became a
 * booking, so the operator's next move differs: they get two separately headed
 * tables with their own counts, blurbs and empty states rather than one merged
 * list with a type column. Resolved rows, when revealed, sit in a bordered
 * block under the open table inside the same card.
 */
import { CreditCard, Ticket } from 'lucide-react';

import type { WorkshopShell } from '../dashboards/workshopDetailUrlFor';

import { partitionByState, sectionCopy } from './reviewQueueModel';
import { ReviewQueueTable } from './ReviewQueueTable';
import type { ReviewKind, ReviewQueueItem } from './reviewTypes';

export interface ReviewQueueSectionProps {
  kind: ReviewKind;
  /** The section's rows, as the API ordered them (oldest first). */
  rows: ReviewQueueItem[];
  /** Reveal the resolved block. Off by default, so the page reads as a to-do
   *  list. */
  showResolved: boolean;
  /** Which dashboards shell the rows' workshop links target. */
  shell: WorkshopShell;
}

/**
 * Section-header geometry. `.nb-display` is an UPPERCASE serif, so the glyphs
 * occupy the band between baseline and cap-height and the line box's descender
 * space is always empty. Centring a 15px icon on that line box therefore lands
 * it off the text's optical centre, which is what made the icon ride high.
 *
 * Two things fix it deterministically: `lineHeight: 1` collapses the heading's
 * box onto its em box, and the row centres rather than baseline-aligns, so
 * nothing depends on a flex container's synthesized baseline (which, for a group
 * led by an SVG, is the icon's bottom edge — the actual culprit). None of these
 * values read `--nb-*`, so the result is identical at both densities.
 */
const HEADER_ROW = { display: 'flex', alignItems: 'center', gap: 10 } as const;

const TITLE_GROUP = {
  display: 'flex',
  alignItems: 'center',
  gap: 7,
  color: 'var(--nb-bark-dark)',
} as const;

const ICON = { flex: '0 0 15px', display: 'block' } as const;

const TITLE = { margin: 0, fontSize: 16, lineHeight: 1 } as const;

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ padding: '30px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--nb-muted)' }}>{title}</div>
      <div style={{ fontSize: 11.5, color: 'var(--nb-subtle)', marginTop: 3 }}>{body}</div>
    </div>
  );
}

/** Horizontal scroll for the table alone. `min-width` keeps the six columns at
 *  their intended sizes; without it they compress to fit and the reason column
 *  wins nothing. */
const TABLE_SCROLL = { overflowX: 'auto' as const, minWidth: 0 };

export function ReviewQueueSection({ kind, rows, showResolved, shell }: ReviewQueueSectionProps) {
  const copy = sectionCopy(kind);
  const { open, resolved } = partitionByState(rows);
  const Icon = kind === 'booking' ? Ticket : CreditCard;

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={HEADER_ROW}>
        <span style={TITLE_GROUP}>
          <Icon size={15} style={ICON} />
          <h2 className="nb-display" style={TITLE}>
            {copy.title}
          </h2>
        </span>
        <span
          data-testid="section-count"
          style={{
            fontSize: 12,
            fontWeight: 700,
            color: open.length ? 'var(--nb-warn)' : 'var(--nb-subtle)',
          }}
        >
          {open.length}
        </span>
        <span style={{ fontSize: 12, color: 'var(--nb-subtle)', flex: 1, minWidth: 0 }}>
          {copy.blurb}
        </span>
      </div>

      <div className="nb-card nb-card-tight" style={{ padding: 0, overflow: 'hidden' }}>
        {/* U15: six columns do not fit a tablet (768-1023px), where this table was
            simply crushed — the phone breakpoint is 767px, so everything above it
            got the full desktop grid. The table scrolls inside its own container
            rather than squeezing the columns or forcing the PAGE to scroll
            sideways, and `min-width` is what makes the columns keep their sizes
            instead of collapsing to fit. */}
        {open.length > 0 ? (
          <div style={TABLE_SCROLL}>
            <ReviewQueueTable rows={open} kind={kind} shell={shell} />
          </div>
        ) : (
          <EmptyState title={copy.emptyTitle} body={copy.emptyBody} />
        )}
        {showResolved && resolved.length > 0 && (
          <div style={{ borderTop: '1px solid var(--nb-border)', background: 'var(--nb-surface)' }}>
            <div
              className="nb-eyebrow"
              style={{ padding: '8px 18px 2px', color: 'var(--nb-muted)' }}
            >
              Resolved · {resolved.length}
            </div>
            <div style={TABLE_SCROLL}>
              <ReviewQueueTable rows={resolved} kind={kind} shell={shell} muted />
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
